/**
 * Buy credits dialog.
 *
 * Clicking "Continue to checkout" redirects into Stripe Checkout via
 * POST /v1/billing/checkout-session in payment mode; the webhook
 * handler grants credits on success. Custom credit amounts are not
 * supported (Stripe needs a fixed price id), so the user picks one
 * of the configured packs from pricing.yaml.
 *
 * If Stripe isn't configured in this environment the dialog renders
 * an explanatory alert and disables the action button — there is no
 * dummy "grant credits in DB" fallback path.
 */

import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Box, Typography, Card, CardActionArea, Button,
  Stack, Chip, CircularProgress, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { fetchPacks } from '../../redux/actions/billing/topup';
import { startCheckout, clearState as clearCheckoutState } from '../../redux/actions/billing/checkout';
import { formatCredits } from './billingHelpers';

const TopUpDialog = ({
  open, onClose,
  packs, packsLoading,
  loadPacks,
  stripeConfigured, checkoutInFlight, checkoutError,
  doCheckout, doClearCheckout,
}) => {
  const [selectedPack, setSelectedPack] = useState(null);

  useEffect(() => {
    if (open) loadPacks();
  }, [open, loadPacks]);

  useEffect(() => {
    if (!open) {
      setSelectedPack(null);
      doClearCheckout();
    }
  }, [open, doClearCheckout]);

  const inFlight = checkoutInFlight;
  const errorMsg = checkoutError;

  const selectedHasPrice = Array.isArray(packs)
    && !!(packs.find((p) => p.id === selectedPack)?.stripe_price_id);
  const canSubmit = !inFlight && stripeConfigured && !!selectedPack && selectedHasPrice;

  const handleSubmit = () => {
    if (!selectedPack) return;
    doCheckout({ mode: 'payment', pack_id: selectedPack });
  };

  return (
    <Dialog
      open={open}
      onClose={inFlight ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pr: 6 }}>
        Top up credits
        <IconButton
          onClick={onClose}
          disabled={inFlight}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick a credit pack. Credits never expire and apply to any
          billable operation across training, inference, storage, and
          ingestion.
        </Typography>

        {packsLoading && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!packsLoading && (
          <Stack spacing={1.5}>
            {(packs || []).map((p) => (
              <Card
                key={p.id}
                variant="outlined"
                sx={{
                  borderColor: selectedPack === p.id ? 'primary.main' : 'divider',
                  borderWidth: selectedPack === p.id ? 2 : 1,
                  transition: 'border 120ms',
                }}
              >
                <CardActionArea onClick={() => setSelectedPack(p.id)} sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box flex={1}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {p.label}
                        </Typography>
                        {p.save_pct > 0 && (
                          <Chip
                            size="small"
                            color="success"
                            label={`Save ${p.save_pct}%`}
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {formatCredits(p.credits)} credits
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" fontWeight={700}>
                        ${p.usd?.toLocaleString?.() ?? p.usd}
                      </Typography>
                    </Box>
                  </Stack>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        )}

        {errorMsg && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {String(errorMsg)}
          </Alert>
        )}

        {!stripeConfigured && (
          <Alert
            severity="info"
            icon={<LockOutlinedIcon fontSize="small" />}
            sx={{ mt: 2 }}
          >
            Payments are powered by Stripe Checkout, which isn't
            configured in this environment yet. Credit top-ups are
            temporarily unavailable.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={inFlight}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={inFlight
            ? <CircularProgress size={16} color="inherit" />
            : <OpenInNewIcon fontSize="small" />}
        >
          {inFlight ? 'Opening checkout…' : 'Continue to checkout'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  packs:            state.billing_topup.packs,
  packsLoading:     state.billing_topup.packsLoading,
  stripeConfigured: state.billing_subscription?.response?.stripe_configured === true,
  checkoutInFlight: state.billing_checkout?.isLoading,
  checkoutError:    state.billing_checkout?.error,
});

const mapDispatchToProps = {
  loadPacks:        fetchPacks,
  doCheckout:       startCheckout,
  doClearCheckout:  clearCheckoutState,
};

export default connect(mapStateToProps, mapDispatchToProps)(TopUpDialog);
